import { GoogleGenerativeAI, SchemaType } from "@google/generative-ai";
import { PROMPTS } from "./promptRegistry.ts";
import i18n from "../i18n.ts";
import { Project, ComplianceRisk, AiReport, FinancialMetrics, BudgetItem, BudgetForecast, ReportType, ComplianceRequirement, Activity, AuditResult } from "../types.ts";

let _ai: GoogleGenerativeAI | null = null;
const getAI = (): GoogleGenerativeAI => {
  if (!_ai) {
    const apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    if (!apiKey) {
      throw new Error("Gemini API key not configured.");
    }
    _ai = new GoogleGenerativeAI(apiKey);
  }
  return _ai;
};

const STABLE_MODEL = "gemini-1.5-pro-002";
const FLASH_MODEL = "gemini-2.0-flash-exp";

const cleanJson = (text: string | undefined) => {
  if (!text) return "{}";
  return text.replace(/```json\s*|```\s*$/g, "").trim();
};

const getLanguageDirective = () => {
  const lng = i18n.language || 'en';
  return lng.startsWith('ko') ? "IMPORTANT: Respond in Korean (한국어로 응답하세요)." : "IMPORTANT: Respond in English.";
};

export const generateProjectBrief = async (
  project: Project,
  metrics: FinancialMetrics | null,
  risks: ComplianceRisk[],
  compliance: ComplianceRequirement[]
): Promise<{ narrative: string; healthScore: number; keyTakeaway: string; marginRisk: string }> => {
  const riskSummary = risks.map(r => `[${r.level}] ${r.summary}`).join("; ");
  const metricSummary = metrics ? `Cost Variance: ${metrics.variance}, Burn: ${metrics.burnRate}%, Earned: ${metrics.earnedValue}` : "No metrics available.";

  const model = getAI().getGenerativeModel({
    model: FLASH_MODEL,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          narrative: { type: SchemaType.STRING },
          healthScore: { type: SchemaType.NUMBER },
          keyTakeaway: { type: SchemaType.STRING },
          marginRisk: { type: SchemaType.STRING }
        },
        required: ["narrative", "healthScore", "keyTakeaway", "marginRisk"]
      }
    }
  });

  const result = await model.generateContent(`Act as a hard-nosed General Contractor Operations Director. Your sole priority is MARGIN PROTECTION.
    ${getLanguageDirective()}
    Review these indicators for "${project.title}":
    
    Financials: ${metricSummary}
    Risks: ${riskSummary}
    Compliance: ${compliance.length} pending items.
    
    Identify:
    1. Potential "Free Work" (Unbilled scope creep).
    2. Subcontractor cost exposure.
    3. Profit margin erosion.
    
    Output JSON.`);

  return JSON.parse(cleanJson(result.response.text()));
};

export const forensicLogAudit = async (project: Project, wbs: Activity[], logText: string): Promise<AuditResult> => {
  const wbsItems = wbs.map(t => ({ id: t.id, code: t.wbsCode, title: t.title }));

  const model = getAI().getGenerativeModel({
    model: STABLE_MODEL,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          scopeClassification: { type: SchemaType.STRING, enum: ["InScope", "OutOfScopeLikely", "Ambiguous"], format: "enum" },
          causeType: { type: SchemaType.STRING, enum: ["Owner Request", "Unforeseen Condition", "Design Issue", "Coordination Issue", "Other"], format: "enum" },
          recommendedAction: { type: SchemaType.STRING, enum: ["NoAction", "ClarifyViaRFI", "DraftCO", "FlagForPMReview"], format: "enum" },
          confidenceScore: { type: SchemaType.NUMBER },
          wbsMatches: {
            type: SchemaType.ARRAY,
            items: {
              type: SchemaType.OBJECT,
              properties: {
                id: { type: SchemaType.STRING },
                confidence: { type: SchemaType.NUMBER },
                rationale: { type: SchemaType.STRING }
              }
            }
          },
          summary: { type: SchemaType.STRING },
          notesForPM: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } }
        },
        required: ["scopeClassification", "causeType", "recommendedAction", "wbsMatches", "summary", "notesForPM", "confidenceScore"]
      }
    }
  });

  const result = await model.generateContent(`SYSTEM: You are a Forensic Construction Claims Expert.
    ${getLanguageDirective()}
    TASK: Audit this field log. Project Magnitude: ${project.originalContractValue}. WBS Baseline: ${JSON.stringify(wbsItems)}. Field Entry: "${logText}"`);

  return JSON.parse(cleanJson(result.response.text()));
};

export const generateCONarrative = async (
  project: Project,
  logContent: string,
  auditResult: AuditResult,
  wbsContext: string
): Promise<{ title: string; summary: string; narrative: string }> => {

  const model = getAI().getGenerativeModel({
    model: STABLE_MODEL,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          title: { type: SchemaType.STRING },
          summary: { type: SchemaType.STRING },
          narrative: { type: SchemaType.STRING }
        },
        required: ["title", "summary", "narrative"]
      }
    }
  });

  const result = await model.generateContent(`Act as a Forensic Claims Consultant. Create a Change Order draft.
    ${getLanguageDirective()}
    Project: ${project.title}. WBS Context: ${wbsContext}. Field Observation: "${logContent}". Audit Findings: ${auditResult.summary}`);

  return JSON.parse(cleanJson(result.response.text()));
};

export const auditChangeOrder = async (title: string, amount: number, desc: string, logs: string): Promise<string> => {
  const model = getAI().getGenerativeModel({ model: STABLE_MODEL });
  const result = await model.generateContent(`Generate a firm, professional Change Order justification for "${title}" ($${amount}). ${getLanguageDirective()} Basis: ${desc}. Field History: ${logs}.`);
  return result.response.text();
};

export const scanIntelligence = async (base64Data: string, mimeType: string): Promise<{ risks: ComplianceRisk[], deadlines: ComplianceRequirement[], wbs: Partial<Activity>[] }> => {
  const model = getAI().getGenerativeModel({
    model: FLASH_MODEL,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          risks: { type: SchemaType.ARRAY, items: { type: SchemaType.OBJECT, properties: { summary: { type: SchemaType.STRING }, level: { type: SchemaType.STRING }, mitigationPlan: { type: SchemaType.STRING } } } },
          deadlines: { type: SchemaType.ARRAY, items: { type: SchemaType.OBJECT, properties: { title: { type: SchemaType.STRING }, dueDate: { type: SchemaType.STRING }, requirementType: { type: SchemaType.STRING }, description: { type: SchemaType.STRING } } } },
          wbs: { type: SchemaType.ARRAY, items: { type: SchemaType.OBJECT, properties: { wbsCode: { type: SchemaType.STRING }, title: { type: SchemaType.STRING } } } }
        }
      }
    }
  });

  const data = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;
  const result = await model.generateContent([
    PROMPTS.INTELLIGENCE_SCAN + "\n" + getLanguageDirective(),
    { inlineData: { mimeType, data } }
  ]);
  return JSON.parse(cleanJson(result.response.text()));
};

export const generateComprehensiveReport = async (
  project: Project, type: ReportType, instr: string, fin: FinancialMetrics | null, budget: BudgetItem[], risks: ComplianceRisk[]
): Promise<AiReport> => {
  const model = getAI().getGenerativeModel({
    model: STABLE_MODEL,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: { title: { type: SchemaType.STRING }, statusColor: { type: SchemaType.STRING }, summaryText: { type: SchemaType.STRING }, recommendations: { type: SchemaType.ARRAY, items: { type: SchemaType.STRING } } }
      }
    }
  });

  const content = PROMPTS.COMPREHENSIVE_REPORT(project.title, project.status, fin ? `CV: ${fin.variance}` : 'N/A', risks.map(r => r.summary).join(','), instr) + "\n" + getLanguageDirective();
  const result = await model.generateContent(content);
  const res = JSON.parse(cleanJson(result.response.text()));

  return { projectId: project.id!, reportType: type, title: res.title, createdAt: new Date().toISOString(), summaryText: res.summaryText, statusColor: res.statusColor, recommendations: res.recommendations };
};

export const refineRiskMitigation = async (projectTitle: string, riskSummary: string, currentPlan: string): Promise<string> => {
  const model = getAI().getGenerativeModel({ model: FLASH_MODEL });
  const result = await model.generateContent(PROMPTS.REFINE_MITIGATION(projectTitle, riskSummary, currentPlan) + "\n" + getLanguageDirective());
  return result.response.text();
};

export const refineIssueResolution = async (projectTitle: string, issueDesc: string, priority: string): Promise<string> => {
  const model = getAI().getGenerativeModel({ model: FLASH_MODEL });
  const result = await model.generateContent(PROMPTS.RESOLVE_ISSUE(projectTitle, issueDesc, priority) + "\n" + getLanguageDirective());
  return result.response.text();
};

export const draftProposalSection = async (project: Project, section: string, ctx: string, tone: string): Promise<string> => {
  const model = getAI().getGenerativeModel({ model: STABLE_MODEL });
  const result = await model.generateContent(PROMPTS.PROPOSAL_DRAFT(project.title, section, ctx, tone) + "\n" + getLanguageDirective());
  return result.response.text();
};

export const auditScopeGap = async (projectTitle: string, wbs: Activity[], observation: string): Promise<{ isGap: boolean, impactLevel: string, justification: string }> => {
  const wbsItems = wbs.map(t => `${t.wbsCode}: ${t.title}`).join(', ');

  const model = getAI().getGenerativeModel({
    model: STABLE_MODEL,
    generationConfig: {
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          isGap: { type: SchemaType.BOOLEAN },
          impactLevel: { type: SchemaType.STRING, enum: ["Low", "Medium", "High"], format: "enum" },
          justification: { type: SchemaType.STRING }
        },
        required: ["isGap", "impactLevel", "justification"]
      }
    }
  });

  const result = await model.generateContent(PROMPTS.SCOPE_AUDIT(projectTitle, wbsItems, observation) + "\n" + getLanguageDirective());
  return JSON.parse(cleanJson(result.response.text()));
};

export const generateBudgetForecast = async (project: Project, history: FinancialMetrics[], items: BudgetItem[]): Promise<BudgetForecast> => {
  const model = getAI().getGenerativeModel({
    model: STABLE_MODEL,
    generationConfig: { responseMimeType: "application/json" }
  });

  const result = await model.generateContent(PROMPTS.BUDGET_FORECAST(project.title, project.originalContractValue, history.length.toString(), items.length.toString()) + "\n" + getLanguageDirective());
  return JSON.parse(cleanJson(result.response.text()));
};
